import { ReactElement } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

import { MainLayout } from "@/containers/layout/LandingLayout";
import routes from "@/constants/routes";

const TermsOfUse = () => {
  return (
    <>
      <Head>
        <title>Keyzy | Terms of Use</title>
        <link rel="canonical" href="https://www.keyzy.com/terms-of-use" />
        <meta
          name="description" key="description"
          content="Review Keyzy's Terms of Use to understand the guidelines and other information for how this website can be used by guests and registered site users."
        />
        <meta
          name="twitter:card"
          content="Review Keyzy's Terms of Use to understand the guidelines and other information for how this website can be used by guests and registered site users."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta property="og:image" content="/landing/keyzy-meta-image.png" key="ogimage" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content="Keyzy | Terms of Use" key="ogtitle" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Review Keyzy's Terms of Use to understand the guidelines and other information for how this website can be used by guests and registered site users."
          key="ogdesc"
        />
      </Head>

      <div className="relative px-5 mt-10 mb-10 xl:mb-14 xl:mt-20">
        <div className="relative text-left mb-16 xl:h-32 xl:text-center">
          <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>

          <h1 className="font-bold max-w-[52.75rem] mx-auto xl:max-w-none text-4xl xs:text-[44px] md:text-[56px] xl:text-[76px] xs:leading-[46px] md:leading-[59px] xl:leading-[80px]">
            Terms of Use
          </h1>
          <div className="font-serif text-3xl mt-6 break-words font-normal">
            Read through our website terms of use
          </div>

          <div className="absolute hidden -right-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles 2"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>
        </div>

        <div className="flex flex-col max-w-[52.75rem] w-full mx-auto">
          <ol>
            <li className="mb-16">
              <h2 className="mb-5 text-xl font-bold">1. Introduction</h2>
              <p>
                We have provided terms of use (hereafter the “Terms”) setting out how this website,
                being www.keyzy.com and any related subdomains (hereafter the “Website”) may be used
                by guests and registered users, and outlining your rights and our rights in relation
                to its use. We advise, for your best interest, to read these Terms carefully. Please
                be aware that the Terms may change from time to time, and we encourage you to
                revisit this page on a regular basis to ensure you are familiar with the latest
                version.
                <br className="br-2x" />
                Kollitom Ltd (registered in England and Wales, Company No. 13075506) has its
                registered office at LABS Atrium, Chalk Farm Rd, London, NW1 8AH.
                <br className="br-2x" />
                Keyzy is a trading name of Kollitom Ltd.
                <br className="br-2x" />
                No aspect of the Kollitom’s business falls within the scope of financial services
                regulation. This means you do not have any of protections under the FCA rules and do
                not have any cover from the Financial Ombudsman Scheme or the Financial Services
                Compensation Scheme in relation to tenancy and future purchase agreement with
                Kollitom. Customers should ensure they understand what this means before they use
                the Keyzy product.
                <br className="br-2x" />
                Kollitom is a member of The Property Redress Scheme (membership number PRS030919),
                which covers activities in relation to the purchase of the home, the setting up and
                management of tenancy and the management of maintenance of the home.
                <br className="br-2x" />
                Please note that references on this page to &lsquo;our&lsquo;, &lsquo;we&lsquo; and
                &lsquo;us&lsquo; have the meaning of Kollitom Ltd and its subsidiaries.
                <br className="br-2x" />
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">2. General Terms of Use</h2>
              <p>
                This Website is operated and maintained by Kollitom. In choosing to access this
                Website, you acknowledge that you understand and accept these Terms, as well as
                agreeing to abide by any applicable UK laws and regulations.
                <br className="br-2x" />
                Minors or people below 18 years old are not allowed to use this Website.
                <br className="br-2x" />
                This Website is intended to provide information about Keyzy’s rent to buy solution
                that Customers may be eligible for, subject to the required Customer and Property
                checks and processes undertaken as part of the Keyzy Tenancy Agreement and Future
                Purchase Agreement.
                <br className="br-2x" />
                This Website may provide links to third party websites over which Keyzy has no
                control. These links are provided for your convenience and Keyzy accepts no
                responsibility for the content on such websites.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">3. No Offer</h2>
              <p>
                The information presented on this Website does not constitute an offer to buy, sell
                or subscribe to a financial instrument, nor does it constitute the provision of any
                regulated service by Keyzy or its affiliates (nor any agreement to provide a
                regulated service). Nothing on this Website should be interpreted as investment
                advice, recommendations, legal or tax advice, nor should any information or content
                be relied upon in determining the suitability of the Tenancy Agreement and Future
                Purchase Agreement for you, or in making an investment or other decision.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">4. Intellectual Property</h2>
              <p>
                This Website and its contents are the property of Keyzy, and where applicable third
                parties, and are protected by applicable copyright law with all rights reserved.
                <br className="br-2x" />
                You may download or print a hard copy of individual pages and/or sections of this
                Website, provided that you do not remove any copyright or other proprietary notices.
                Any downloading or other copying from this Website will not transfer title to any
                software or material to you.
                <br className="br-2x" />
                You may not publish, reproduce (in whole or part), transmit (by electronic means or
                otherwise), modify, link or use for any public or commercial purposes this Website
                (including its data) without the prior permission of Keyzy.
                <br className="br-2x" />
                Nothing on this Website shall be construed as granting a license or right to use any
                image, trademark, or logo, all of which are the property of Keyzy. Keyzy reserves
                all rights with respect to its proprietary information or material on this Website
                and will enforce such rights to the full extent of applicable copyright and
                trademark law.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">5. Further Restrictions</h2>
              <p>
                You may not use this Website for illegal or unlawful or malicious activities, or for
                activities that we deem improper for any reason whatsoever in our sole judgment,
                including any data mining, data harvesting, data extracting or any other similar
                activity in relation to this Website.
                <br className="br-2x" />
                You may not impersonate any person or use or provide any fraudulent, misleading or
                inaccurate information, or access or use (or attempt to access or use) another
                user’s account without permission.
                <br className="br-2x" />
                You may not use this Website in any way that is or may be damaging to this Website
                and/or Keyzy or its affiliates, that impacts user access to this Website, or
                otherwise use this Website contrary to applicable laws and regulations.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">6. No Reliance or Warranty</h2>
              <p>
                Although Keyzy has taken all reasonable care to ensure that the information,
                opinions, and references to third parties contained within this Website are
                accurate, no representation or warranty (including liability towards third parties),
                expressed or implied, is made as to its accuracy, reliability or completeness by
                Keyzy or its partners. Keyzy also give no warranty that this Website will be free
                from faults and/or defects (including viruses or other harmful components), and
                accept no liability for this or any disruption or loss of availability of this
                Website or any part of it. You use this Website and its content at your own risk.
                <br className="br-2x" />
                The information, and opinions expressed on this Website are subject to change and
                may be modified at any time and without prior warning.
                <br className="br-2x" />
                We do not warrant the adequacy, accuracy, reliability or completeness of any
                information on hyperlinked or referenced third party websites and expressly disclaim
                any liability for any and all of their content. A hyperlink to another Website is
                meant for convenience only and does not imply that Keyzy endorses or approves of the
                Website or any products or services that it describes.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">7. Limitation of Liability</h2>
              <p>
                Our liability is limited to intent and gross negligence. Nothing in these Terms
                shall exclude or limit our liability for death or personal injury caused by
                negligence, fraud or fraudulent misrepresentation, or any liability which cannot be
                excluded or limited under applicable law.
                <br className="br-2x" />
                Keyzy, including its officers, directors and employees shall not be held liable for
                any indirect, consequential or special liability arising out of or in any way
                related to your use of this Website.
                <br className="br-2x" />
                You hereby indemnify to the fullest extent possible from and against any and/or all
                liabilities, costs, demands, causes of action, damages and expenses arising in any
                way related to your breach of any of the provisions of these Terms.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">8. Information Submitted By You</h2>
              <p>
                All content submitted by you through this Website must be your own and must not be
                invading any third-party’s rights.
                <br className="br-2x" />
                Any information submitted by you through this Website shall be handled in accordance
                with our{" "}
                <Link href={routes.privacy}>
                  <a className="underline">Privacy Policy</a>
                </Link>
                .
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">9. Your Account</h2>
              <p>
                You must keep your account details safe and confidential, which includes but is not
                limited to any user identification credentials, password and other information
                required as part of our login or security procedures.
                <br className="br-2x" />
                We have the right to disable any user identification credentials or password, at any
                time, if in our reasonable opinion you have failed to comply with any of the
                provisions of these Terms. If you know or suspect that anyone other than you knows
                your user identification code or password, you must promptly notify us at{" "}
                <Link href={routes.home}>
                  <a className="underline">hello@keyzy.com</a>
                </Link>
                .
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">10. Affiliation</h2>
              <p>
                You must not establish a link to this Website in such a way as to suggest any form
                of association, approval or endorsement on our part where none exists.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">11. Reservation of Rights</h2>
              <p>
                We reserve the right to change, modify, add to, or remove portions of these Terms at
                any time.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">12. Assignment</h2>
              <p>
                Keyzy is allowed to assign, transfer, and subcontract its rights and/or obligations
                under these Terms without any notification. We will always give you notice before
                doing this. However, you are not allowed to assign, transfer, or subcontract any of
                your rights and/or obligations under these Terms.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">13. Governing Law</h2>
              <p>
                Any action arising out of these terms or this Website shall be litigated in, and
                only in, courts located in England and Wales. In the event that any provision of
                these Terms is held unenforceable, the validity or enforceability of the remaining
                provisions will not be affected.
              </p>
            </li>

            <li className="mb-14">
              <h2 className="mb-5 text-xl font-bold mt-7">14. Contact Us</h2>
              <p>
                You can contact us about these Terms through emailing{" "}
                <a
                  href="mailto:hello@keyzy.com"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  hello@keyzy.com
                </a>
              </p>
            </li>
          </ol>
        </div>
      </div>
    </>
  );
};

TermsOfUse.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};

export default TermsOfUse;
